import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Loader2, Paperclip, Send, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Sheet, SheetContent } from '@easyflow/ui-frontend/components/ui/sheet';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { aurora, auroraPalette } from '@/components/aurora/tokens';

interface MobileComposerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /** Placeholder area testo. */
  placeholder?: string;
  /** Valore controllato (testo). */
  value: string;
  onValueChange: (v: string) => void;
  /** Callback di invio. Restituire false/throw lascia il sheet aperto. */
  onSubmit: () => Promise<void> | void;
  submitting?: boolean;
  /** Etichetta CTA invio. Default: "Invia". */
  submitLabel?: string;
  /** Disabilita la CTA invio (es. testo vuoto + nessun allegato). */
  submitDisabled?: boolean;
  /** Callback selezione allegati (lasciato al chiamante: file picker, gallery). */
  onPickAttachment?: () => void;
  /** Lista allegati già selezionati, gestita dal chiamante. */
  attachments?: ReactNode;
  /**
   * Slot extra in alto (es. selettore destinatari, picker poll, audience).
   * Renderizzato sopra il textarea.
   */
  extra?: ReactNode;
  /** Slot toolbar sotto il textarea (es. formattazione, emoji, mention). */
  toolbar?: ReactNode;
}

/**
 * Bottom sheet fullscreen per composer ricco (post Feed, messaggio canale,
 * messaggio gruppo). Gestisce keyboard inset via visualViewport e safe-area.
 */
export default function MobileComposerSheet({
  open,
  onOpenChange,
  title = 'Nuovo messaggio',
  placeholder = 'Scrivi qui…',
  value,
  onValueChange,
  onSubmit,
  submitting,
  submitLabel = 'Invia',
  submitDisabled,
  onPickAttachment,
  attachments,
  extra,
  toolbar,
}: MobileComposerSheetProps) {
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [kbInset, setKbInset] = useState(0);

  // Auto-focus all'apertura, riducendo flicker keyboard su iOS.
  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => taRef.current?.focus(), 60);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const layoutH = window.innerHeight;
      const visualH = vv.height;
      setKbInset(Math.max(0, layoutH - visualH - (vv.offsetTop || 0)));
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [open]);

  const handleSubmit = async () => {
    if (submitDisabled || submitting) return;
    await onSubmit();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <SheetContent
        side="bottom"
        className="h-[100dvh] max-h-[100dvh] flex flex-col p-0 gap-0"
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
        data-testid="mobile-composer-sheet"
      >
        <header className="flex items-center gap-2 px-3 py-2 border-b border-border min-h-[56px]">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10"
            aria-label="Chiudi"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="mobile-composer-close"
          >
            <X className="w-5 h-5" />
          </Button>
          <h2 className={cn(aurora.type.title, 'flex-1 text-foreground truncate')}>{title}</h2>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!!submitDisabled || !!submitting}
            className="h-10 px-4 gap-2 rounded-full"
            style={{ background: auroraPalette.indigo }}
            data-testid="mobile-composer-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {submitLabel}
          </Button>
        </header>

        {extra && (
          <div className="px-3 py-2 border-b border-border" data-testid="mobile-composer-extra">
            {extra}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-3 py-3">
          <Textarea
            ref={taRef}
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            placeholder={placeholder}
            className="w-full min-h-[40dvh] border-none resize-none text-base leading-relaxed bg-transparent"
            data-testid="mobile-composer-textarea"
          />
          {attachments && <div className="mt-2" data-testid="mobile-composer-attachments">{attachments}</div>}
        </div>

        <div
          className="border-t border-border px-2 py-2 flex items-center gap-1"
          style={{ paddingBottom: `calc(env(safe-area-inset-bottom) + ${kbInset}px + 8px)` }}
          data-testid="mobile-composer-toolbar"
        >
          {onPickAttachment && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-10 w-10"
              aria-label="Allega file"
              onClick={onPickAttachment}
              disabled={submitting}
              data-testid="mobile-composer-attach"
            >
              <Paperclip className="w-5 h-5" />
            </Button>
          )}
          <div className="flex-1 overflow-x-auto">{toolbar}</div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
